import React, { useEffect, useState } from "react";
import md5 from "md5";
import {
  NOT_ALLOWED_NAME_REGEX,
  INVALID_NAME_ERROR_MESSAGE,
  ONBOARDING_STEPS,
  GQL_FETCH_POLICY_CACHE_AND_NETWORK_ONLY,
} from "../../../constants";

// Utils
import { nameCheck, isPasswordValid } from "../../../utilities/userInfo";
import isKey from "../../../utilities/keyboard-events";

// GQL
import { GET_APPLICATION_INFO } from "../../../api/gql";
import { useCurrentStep, usePromisedLazyQuery } from "../../../state/store/customHooks";

/* Components */
import ToggleVisibility from "../../common/ToggleVisibility";
import ContinueButton from "../common/ContinueButton";
import { ReadOnly } from "../../common";

const BasicInfoForm = ({ email, referralCode, _completeStep, loading }) => {
  const currentStep = useCurrentStep();
  const readOnly = currentStep !== ONBOARDING_STEPS.BasicInfo;
  const [getApplicationInfo] = usePromisedLazyQuery(GET_APPLICATION_INFO, GQL_FETCH_POLICY_CACHE_AND_NETWORK_ONLY);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  // Placeholder exists for users to see the password form filled out when you're in
  // a read only state. Consider skipping password validation when read only is true
  const [password, setPassword] = useState(readOnly ? "PasswordPlaceholder1!" : '');
  const [showPassword, setShowPassword] = useState(false);

  const [errorFirstName, setErrorFirstName] = useState('');
  const [errorLastName, setErrorLastName] = useState('');
  const [errorPassword, setErrorPassword] = useState('');

  useEffect(() => {
    const initializeNames = async () => {
      if(!readOnly)
        return;
      const applicationInfo = await getApplicationInfo();
      const {
        firstName: firstNameFromDb,
        lastName: lastNameFromDb,
      } = applicationInfo.getApplicationInfo;

      setFirstName(firstNameFromDb);
      setLastName(lastNameFromDb);
    };

    initializeNames();
  }, []);

  // helper functions
  const isFormValid = () => {
    const validFirstName = nameCheck(firstName);
    const validLastName = nameCheck(lastName);
    const validPassword = isPasswordValid(password);

    if(!validFirstName)
      setErrorFirstName(INVALID_NAME_ERROR_MESSAGE);

    if(!validLastName)
      setErrorLastName(INVALID_NAME_ERROR_MESSAGE);

    if(validPassword.error)
      setErrorPassword(validPassword.error);

    if(!validFirstName || !validLastName || validPassword.error)
      return false;

    return true;
  };

  const onSubmission = () => {
    if(!isFormValid())
      return;

    const payload = {
      email: email.toLowerCase(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      password: md5(password),
      referralCode,
      source: "CARD_ONBOARDING", // TODO: Figure out why common-fe imports are broken for onboarding
    };
    _completeStep(payload);
  };

  return (
    <div onKeyDown={(e) => {
      if(isKey(e, ["enter"]))  onSubmission();
    }}>
      <ReadOnly readOnly={readOnly}>
        <form>
          {errorFirstName && (
            <div className="errorText">{errorFirstName}</div>
          )}
          <input
            autoComplete="new-password"
            type="text"
            data-hj-allow
            name="firstname"
            value={firstName}
            placeholder="Legal first name"
            className={`inputField inputFieldText form-control data-hj-allow ${
              errorFirstName ? "redBorder" : ""
            }`}
            onChange={(e) => {
              let { value } = e.target;
              value = value.replace(NOT_ALLOWED_NAME_REGEX, '');
              setFirstName(value);
            }}
            onFocus={() => setErrorFirstName('')}
          />
          <br />
          {errorLastName && (
            <div className="errorText">{errorLastName}</div>
          )}
          <input
            autoComplete="new-password"
            type="text"
            data-hj-allow
            name="lastname"
            value={lastName}
            placeholder="Legal last name"
            className={`inputField inputFieldText form-control data-hj-allow ${
              errorLastName ? "redBorder" : ""
            }`}
            onChange={(e) => {
              let { value } = e.target;
              value = value.replace(NOT_ALLOWED_NAME_REGEX, '');
              setLastName(value);
            }}
            onFocus={() => setErrorLastName('')}
          />
          <br />
          <input
            autoComplete="new-password"
            type="text"
            data-hj-allow
            name="email"
            value={email}
            placeholder="Email"
            title="Your email cannot be changed"
            disabled={!!email}
            className="inputField inputFieldText form-control data-hj-allow text-gray-700 cursor-not-allowed"
          />
          <br />
          {errorPassword && (
            <div className="errorText">{errorPassword}</div>
          )}
          {/* NOTE: Password show/hide button will overlap long passwords on small screens */}
          <input
            autoComplete="new-password"
            type={showPassword ? "text" : "password"}
            name="password"
            value={password}
            placeholder="Password"
            className={`inputField inputFieldText ${
              errorPassword ? "redBorder" : ""
            }`}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
            onFocus={() => {
              setErrorPassword(null);
            }}
          />
          <div
            style={{
              position: "relative",
              display: "inline-block",
              height: 0,
              width: 0,
            }}
          >
            <ToggleVisibility
              visible={showPassword}
              onClick={() => {
                setShowPassword(!showPassword);
              }}
            />
          </div>
          <p className="text-xs text-gray-500 text-left px-4">
            Must contain at least 8 characters and 1 letter and 1 symbol.
          </p>

        </form>
      </ReadOnly>
      <ContinueButton
        buttonText={readOnly ? "Continue" : "Get Started"}
        disabled={loading}
        onClick={onSubmission}
      />
    </div>
  );
};

export default BasicInfoForm;